import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

import { SectionCard } from "../../../Component/CustComponents/SectionCard";
import { formatCurrency } from "../../../Component/CustComponents/formatCurrency";
import { Badge } from "../../../Component/CustComponents/Badge";
import Pagination from "../../../Component/Common/Pagination";
import RefreshButton from "../../../Component/Common/RefreshButton";

import RaiseClaimForm from "./ClaimsProcess/RaiseClaimForm";
import { BASE_URL } from "../../../config/api";

/* ------------------------------------------------------
   MOCK CLAIMS (TABLE ONLY)
------------------------------------------------------ */
const initialClaims = [
  {
    id: "C-CLM-5001",
    warrantyId: "18",
    productName: "Vivo Screen Damage",
    issueSummary: "Device not powering on",
    raisedOn: "2025-10-10",
    status: "Approved",
    adminMessage: "Approved, pickup scheduled",
    lastUpdated: "2025-10-15",
    approvedAmount: 3500,
    payableAmount: 3000,
    payableStatus: "Paid",
  },
];

export default function ClaimsView() {
  const location = useLocation();
  const filterStatus = location.state?.filterStatus || "all";
  const fromKPI = location.state?.fromKPI || false;

  /* ---------------- STATE ---------------- */
  const [claims, setClaims] = useState(initialClaims);
  const [customer, setCustomer] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* ---------------- CUSTOMER ID ---------------- */
  const authUser = JSON.parse(localStorage.getItem("authUserData"));
  const customerId = authUser?.id;

  /* ---------------- FETCH CUSTOMER DETAILS ---------------- */
  useEffect(() => {
    if (!customerId) return;

    const fetchCustomerDetails = async () => {
      try {
        setLoadingCustomer(true);
        const res = await axios.get(
          `${BASE_URL}warranty/get-customer-details?id=${customerId}`
        );
        setCustomer(res.data);
      } catch (err) {
        console.error("Failed to fetch customer details", err);
      } finally {
        setLoadingCustomer(false);
      }
    };

    fetchCustomerDetails();
  }, [customerId]);

  /* ---------------- HANDLE NEW CLAIM ---------------- */
  const handleNewClaim = (form) => {
    setSubmitting(true);

    const selectedDevice = customer.devices.find(
      (d) => d.id === Number(form.warrantyId)
    );

    const newClaim = {
      id: `C-CLM-${5000 + claims.length + 1}`,
      warrantyId: String(form.warrantyId),
      productName: selectedDevice?.product_name || "Unknown Product",
      issueSummary: form.issueSummary,
      raisedOn: new Date().toISOString().slice(0, 10),
      status: "Pending",
      adminMessage: "Submitted. Our team will review your claim.",
      lastUpdated: new Date().toISOString().slice(0, 10),
      approvedAmount: 0,
      payableAmount: 0,
      payableStatus: "Pending",
    };

    setTimeout(() => {
      setClaims((prev) => [newClaim, ...prev]);
      setSubmitting(false);
    }, 600);
  };

  /* ---------------- FILTER + PAGINATION ---------------- */
  const filteredClaims =
    filterStatus === "all"
      ? claims
      : claims.filter((c) => c.status === filterStatus);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(filteredClaims.length / itemsPerPage);

  const paginatedClaims = filteredClaims.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  /* ---------------- UI ---------------- */
  return (
    <div className="space-y-4">
      {/* ---------------- RAISE CLAIM ---------------- */}
      {!fromKPI && (
        <RaiseClaimForm
          customer={customer}
          warranties={customer?.devices || []}
          warrantiesLoading={loadingCustomer}   // 👈 IMPORTANT
          submitting={submitting}
          onSubmitClaim={handleNewClaim}
        />
      )}

      {/* ---------------- CLAIMS TABLE ---------------- */}
      <SectionCard title="My Claims">
        <div className="flex justify-end mb-2">
          <RefreshButton />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b text-slate-500">
                <th className="py-2 text-left">Claim ID</th>
                <th className="py-2 text-left">Product</th>
                <th className="py-2 text-left">Amounts</th>
                <th className="py-2 text-left">Issue</th>
                <th className="py-2 text-left">Raised</th>
                <th className="py-2 text-left">Status</th>
                <th className="py-2 text-left">Resolution</th>
              </tr>
            </thead>

            <tbody>
              {paginatedClaims.map((c) => {
                const color =
                  c.status === "Approved"
                    ? "green"
                    : c.status === "Rejected"
                    ? "red"
                    : "yellow";

                return (
                  <tr key={c.id} className="border-b bg-white hover:bg-blue-50/50">
                    <td className="py-1.5 font-medium">{c.id}</td>

                    <td className="py-1.5">
                      {c.productName}
                      <div className="text-[10px] text-slate-500">
                        Warranty ID: {c.warrantyId}
                      </div>
                    </td>

                    <td className="py-1.5">
                      Approved: ₹{formatCurrency(c.approvedAmount)}
                      <div className="text-[10px] text-slate-500">
                        Payable: ₹{formatCurrency(c.payableAmount)}
                      </div>
                    </td>

                    <td className="py-1.5">{c.issueSummary}</td>
                    <td className="py-1.5">{c.raisedOn}</td>

                    <td className="py-1.5">
                      <Badge color={color}>{c.status}</Badge>
                    </td>

                    <td className="py-1.5">
                      {c.adminMessage}
                      <div className="text-[10px] text-slate-500">
                        Updated: {c.lastUpdated}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex justify-center mt-3">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}




// import React, { useState } from "react";
// import { PlusCircle } from "lucide-react";
// import { SectionCard } from "../../../Component/CustComponents/SectionCard";
// import { formatCurrency } from "../../../Component/CustComponents/formatCurrency";
// import { Badge } from "../../../Component/CustComponents/Badge";
// import { useLocation } from "react-router-dom";
// import Pagination from "../../../Component/Common/Pagination";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import RaiseClaimForm from "./ClaimsProcess/RaiseClaimForm";

// // ------------------------------------------------------
// // MOCK WARRANTIES (moved inside this file)
// // ------------------------------------------------------
// const initialWarranties = [
//   {
//     id: "C-WAR-1001",
//     productName: "Smartphone",
//     serialOrImei: "356789012345678",
//     expiryDate: "2026-08-14",
//     status: "Active",
//   },
//   {
//     id: "C-WAR-1002",
//     productName: "Tablet",
//     serialOrImei: "351234567890123",
//     expiryDate: "2026-04-20",
//     status: "Active",
//   },
//   {
//     id: "C-WAR-0998",
//     productName: "Demo Device",
//     serialOrImei: "352345678901234",
//     expiryDate: "2025-01-09",
//     status: "Expired",
//   },
// ];

// // ------------------------------------------------------
// // MOCK CLAIMS (moved inside this file)
// // ------------------------------------------------------
// const initialClaims = [
//   {
//     id: "C-CLM-5001",
//     warrantyId: "C-WAR-1001",
//     productName: "Smartphone",
//     issueSummary: "Device not powering on",
//     raisedOn: "2025-10-10",
//     status: "Approved",
//     adminMessage: "Approved, pickup scheduled",
//     lastUpdated: "2025-10-15",
//     approvedAmount: 3500,
//     payableAmount: 3000,
//     payableStatus: "Paid",
//   },
//   {
//     id: "C-CLM-5002",
//     warrantyId: "C-WAR-0998",
//     productName: "Smartphone",
//     issueSummary: "Screen flickering",
//     raisedOn: "2025-01-05",
//     status: "Rejected",
//     adminMessage: "Warranty expired.",
//     lastUpdated: "2025-01-07",
//     approvedAmount: 0,
//     payableAmount: 0,
//     payableStatus: "Not Applicable",
//   },
// ];

// export default function ClaimsView() {
//   // Local states (no props needed)
//   const [claims, setClaims] = useState(initialClaims);
//   const [submitting, setSubmitting] = useState(false);
//   const [claimForm, setClaimForm] = useState({
//     warrantyId: "",
//     issueSummary: "",
//     extraDetails: "",
//   });


//   const location = useLocation();
//   const filterStatus = location.state?.filterStatus || "all";
//   const fromKPI = location.state?.fromKPI || false;
//   // ------------------------------------------------------
//   // Submit handler (fully local)
//   // ------------------------------------------------------
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     if (!claimForm.warrantyId || !claimForm.issueSummary) {
//       alert("Please select a warranty and describe the issue.");
//       return;
//     }

//     setSubmitting(true);

//     const selectedWarranty = initialWarranties.find(
//       (w) => w.id === claimForm.warrantyId
//     );

//     const newClaim = {
//       id: `C-CLM-${5000 + claims.length + 1}`,
//       warrantyId: claimForm.warrantyId,
//       productName: selectedWarranty?.productName || "Unknown Product",
//       issueSummary: claimForm.issueSummary,
//       raisedOn: new Date().toISOString().slice(0, 10),
//       status: "Pending",
//       adminMessage: "Submitted. Our team will review your claim.",
//       lastUpdated: new Date().toISOString().slice(0, 10),
//       approvedAmount: 0,
//       payableAmount: 0,
//       payableStatus: "Pending",
//     };

//     setTimeout(() => {
//       setClaims((prev) => [newClaim, ...prev]);
//       setClaimForm({ warrantyId: "", issueSummary: "", extraDetails: "" });
//       setSubmitting(false);
//     }, 500);
//   };

//   const filteredClaims =
//   filterStatus === "all"
//     ? claims
//     : claims.filter((c) => c.status === filterStatus);

//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 5; // you can change this number

//   const totalPages = Math.ceil(filteredClaims.length / itemsPerPage);

//   const paginatedClaims = filteredClaims.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handleNewClaim = (form) => {
//   setSubmitting(true);

//   const selectedWarranty = initialWarranties.find(
//     (w) => w.id === form.warrantyId
//   );

//   const newClaim = {
//     id: `C-CLM-${5000 + claims.length + 1}`,
//     warrantyId: form.warrantyId,
//     productName: selectedWarranty?.productName || "Unknown Product",
//     issueSummary: form.issueSummary,
//     raisedOn: new Date().toISOString().slice(0, 10),
//     status: "Pending",
//     adminMessage: "Submitted. Our team will review your claim.",
//     lastUpdated: new Date().toISOString().slice(0, 10),
//     approvedAmount: 0,
//     payableAmount: 0,
//     payableStatus: "Pending",
//   };

//   setTimeout(() => {
//     setClaims((prev) => [newClaim, ...prev]);
//     setSubmitting(false);
//   }, 500);
// };



//   // ------------------------------------------------------
//   // UI
//   // ------------------------------------------------------
//   return (
//     <div className="space-y-4">

//       {!fromKPI && (
      
//       <RaiseClaimForm
//         warranties={initialWarranties.filter(
//           (w) => w.status === "Active"
//         )}
//         submitting={submitting}
//         onSubmitClaim={handleNewClaim}
//       />
//       )}


//       {/* ---------------- Claims Table ---------------- */}
//       <SectionCard title="My Claims">
//         <div className="flex justify-end">
//           <RefreshButton/>
//         </div>
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b text-slate-500">
//                 <th className="py-2 text-left">Claim ID</th>
//                 <th className="py-2 text-left">Warranty / Product</th>
//                 <th className="py-2 text-left">Amounts</th>
//                 <th className="py-2 text-left">Issue</th>
//                 <th className="py-2 text-left">Raised</th>
//                 <th className="py-2 text-left">Status</th>
//                 <th className="py-2 text-left">Resolution</th>
//               </tr>
//             </thead>
//             <tbody>
//               {paginatedClaims.map((c) => {
//                 const color =
//                   c.status === "Approved"
//                     ? "green"
//                     : c.status === "Rejected"
//                     ? "red"
//                     : "yellow";

//                 return (
//                   <tr key={c.id} className="border-b bg-white hover:bg-blue-50/50">
//                     <td className="py-1.5 font-medium">{c.id}</td>

//                     <td className="py-1.5">
//                       <div>{c.productName}</div>
//                       <div className="text-[10px] text-slate-500">Warranty: {c.warrantyId}</div>
//                     </td>

//                     <td className="py-1.5">
//                       Approved: ₹{formatCurrency(c.approvedAmount)}
//                       <div className="text-[10px] text-slate-500">
//                         Payable: ₹{formatCurrency(c.payableAmount)}
//                       </div>
//                     </td>

//                     <td className="py-1.5">{c.issueSummary}</td>

//                     <td className="py-1.5">{c.raisedOn}</td>

//                     <td className="py-1.5">
//                       <Badge color={color}>{c.status}</Badge>
//                     </td>

//                     <td className="py-1.5 text-sm">
//                       {c.adminMessage}
//                       <div className="text-[10px] text-slate-500">
//                         Last updated: {c.lastUpdated}
//                       </div>
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>

//           {totalPages > 1 && (
//             <div className="flex justify-center mt-3">
//               <Pagination
//                 currentPage={currentPage}
//                 totalPages={totalPages}
//                 onPageChange={(page) => setCurrentPage(page)}
//               />
//             </div>
//           )}

//         </div>
//       </SectionCard>

//     </div>
//   );
// }




// import { PlusCircle } from "lucide-react";
// import { SectionCard } from "../../../Component/CustComponents/SectionCard";
// import { formatCurrency } from "../../../Component/CustComponents/formatCurrency";
// import { Badge } from "../../../Component/CustComponents/Badge";

// export default function ClaimsView({
//   warranties,
//   claims,
//   claimForm,
//   setClaimForm,
//   submitting,
//   onSubmit,
// }) {
//   return (
//     <div className="space-y-4">
//       <SectionCard
//         title="Raise a New Claim"
//         right={
//           <span className="hidden md:inline text-sm text-slate-400">
//             Select the device and describe the issue clearly
//           </span>
//         }
//       >
//         <form
//           onSubmit={onSubmit}
//           className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs"
//         >
//           <div className="md:col-span-2 flex flex-col gap-1">
//             <label className="text-sm text-slate-600">
//               Select Warranty *
//             </label>
//             <select
//               className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
//               value={claimForm.warrantyId}
//               onChange={(e) =>
//                 setClaimForm((f) => ({ ...f, warrantyId: e.target.value }))
//               }
//             >
//               <option value="">Choose your product warranty</option>
//               {warranties
//                 .filter((w) => w.status === "Active")
//                 .map((w) => (
//                   <option key={w.id} value={w.id}>
//                     {w.productName} · {w.serialOrImei} · Expires {w.expiryDate}
//                   </option>
//                 ))}
//             </select>
//           </div>

//           <div className="md:col-span-2 flex flex-col gap-1">
//             <label className="text-sm text-slate-600">
//               Issue Summary *
//             </label>
//             <input
//               type="text"
//               className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
//               value={claimForm.issueSummary}
//               onChange={(e) =>
//                 setClaimForm((f) => ({ ...f, issueSummary: e.target.value }))
//               }
//               placeholder="e.g. Device not powering on, speaker not working..."
//             />
//           </div>

//           <div className="md:col-span-2 flex flex-col gap-1">
//             <label className="text-sm text-slate-600">
//               Extra Details (optional)
//             </label>
//             <textarea
//               className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none min-h-[70px]"
//               value={claimForm.extraDetails}
//               onChange={(e) =>
//                 setClaimForm((f) => ({ ...f, extraDetails: e.target.value }))
//               }
//               placeholder="Mention when the issue started, any error messages, etc."
//             />
//           </div>

//           <div className="md:col-span-2 flex justify-between items-center pt-1">
//             <span className="text-sm text-slate-500">
//               By submitting, you confirm that the information shared is true and
//               the device belongs to you.
//             </span>
//             <button
//               type="submit"
//               disabled={submitting}
//               className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
//             >
//               <PlusCircle className="w-3 h-3" />
//               <span>{submitting ? "Submitting..." : "Submit Claim"}</span>
//             </button>
//           </div>
//         </form>
//       </SectionCard>

//       <SectionCard title="My Claims">
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">Claim ID</th>
//                 <th className="py-2 text-left">Warranty / Product</th>
//                 <th className="py-2 text-left">Amounts (₹)</th>
//                 <th className="py-2 text-left">Issue</th>
//                 <th className="py-2 text-left">Raised On</th>
//                 <th className="py-2 text-left">Status</th>
//                 <th className="py-2 text-left">Support / Resolution</th>
//               </tr>
//             </thead>
//             <tbody>
//               {claims.map((c) => {
//                 const color =
//                   c.status === "Approved" || c.status === "Settled"
//                     ? "green"
//                     : c.status === "Rejected"
//                     ? "red"
//                     : "yellow";

//                 return (
//                   <tr
//                     key={c.id}
//                     className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                   >
//                     <td className="py-1.5 text-xs text-slate-700 font-medium">
//                       {c.id}
//                     </td>
//                     <td className="py-1.5 text-xs text-slate-700">
//                       <div className="font-medium">{c.productName}</div>
//                       <div className="text-[10px] text-slate-500">
//                         Warranty: {c.warrantyId}
//                       </div>
//                     </td>
//                     <td className="py-1.5 text-sm text-slate-700">
//                       <div>
//                         Approved: ₹ {formatCurrency(c.approvedAmount || 0)}
//                       </div>
//                       <div className="text-[10px] text-slate-500">
//                         Payable: ₹ {formatCurrency(c.payableAmount || 0)}
//                       </div>
//                       {c.payableStatus && (
//                         <div className="text-[10px] text-slate-500">
//                           Status: {c.payableStatus}
//                         </div>
//                       )}
//                     </td>
//                     <td className="py-1.5 text-sm text-slate-700">
//                       {c.issueSummary}
//                     </td>
//                     <td className="py-1.5 text-xs text-slate-700">
//                       {c.raisedOn}
//                     </td>
//                     <td className="py-1.5 text-xs">
//                       <Badge color={color}>{c.status}</Badge>
//                     </td>
//                     <td className="py-1.5 text-sm text-slate-700">
//                       {c.adminMessage || "-"}
//                       {c.lastUpdated && (
//                         <div className="mt-1 text-[10px] text-slate-500">
//                           Last updated: {c.lastUpdated}
//                         </div>
//                       )}
//                       {c.status === "Settled" && c.resolutionSummary && (
//                         <div className="mt-1 text-[10px] text-emerald-700">
//                           Resolution: {c.resolutionSummary}
//                         </div>
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })}
//               {claims.length === 0 && (
//                 <tr>
//                   <td
//                     colSpan={7}
//                     className="py-4 text-center text-xs text-slate-500"
//                   >
//                     You have not raised any claims yet.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </SectionCard>

//       <SectionCard title="Sample Claim Journey (Mobile / Tablet)">
//         <ol className="list-decimal list-inside text-xs text-slate-600 space-y-1">
//           <li>
//             <span className="font-semibold">Raise claim:</span> You submit a
//             claim from this portal with device IMEI, issue and basic details.
//           </li>
//           <li>
//             <span className="font-semibold">Verification:</span> Our team
//             verifies your warranty, coverage and basic checks on the details
//             shared.
//           </li>
//           <li>
//             <span className="font-semibold">Drop-off / Pickup:</span> Based on
//             your location we either schedule a pickup, or ask you to drop the
//             phone / tablet at our channel partner location.
//           </li>
//           <li>
//             <span className="font-semibold">Partner check-in:</span> Partner
//             store receives the device, does a quick visual check and hands over
//             the device to our logistics team.
//           </li>
//           <li>
//             <span className="font-semibold">Service centre diagnosis:</span>
//             Device is sent to authorised service centre where issue is
//             identified and repair is done under the claim approval.
//           </li>
//           <li>
//             <span className="font-semibold">Return & delivery:</span> After
//             repair and testing, device is sent back to the same store or to
//             your registered address, as per agreed mode.
//           </li>
//           <li>
//             <span className="font-semibold">Claim closure summary:</span> On
//             closing the claim we update details like issue identified, parts
//             replaced / work done and final status, which you can see in the
//             claims table above for settled claims.
//           </li>
//         </ol>
//       </SectionCard>
//     </div>
//   );
// }